import React from "react";
import { Button, Pagination, Spinner, Table } from "@heroui/react";
import { Eye, Search } from "lucide-react";
import { PortalPanel } from "./PortalPanel";
import { useLocale } from "../context/LocaleContext";
import { navigatePortal } from "../lib/navigation";
import { withdrawalStatusLabel } from "../lib/withdrawalStatus";

function formatDate(iso, dateLocale) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(dateLocale, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(iso));
}

function customerInitials(row) {
  const name = row.customer_name?.trim();
  if (name) {
    return name
      .split(/\s+/)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }
  return (row.customer_email?.[0] || "?").toUpperCase();
}

function avatarHue(value = "") {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash % 360);
}

function buildPageItems(page, totalPages) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    return pages;
  }

  pages.push(1);
  if (page > 3) pages.push("ellipsis-left");
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p += 1) {
    pages.push(p);
  }
  if (page < totalPages - 2) pages.push("ellipsis-right");
  pages.push(totalPages);
  return pages;
}

function StatusChip({ status, label }) {
  return <span className={`portal-status-chip portal-status-chip--${status || "default"}`}>{label}</span>;
}

function CustomerCell({ row }) {
  const email = row.customer_email || "";
  const displayName = row.customer_name?.trim() || email || "—";

  return (
    <div className="portal-data-grid__customer">
      <span
        className="portal-data-grid__avatar"
        style={{ background: `hsl(${avatarHue(email)} 42% 42%)` }}
        aria-hidden="true"
      >
        {customerInitials(row)}
      </span>
      <span className="portal-data-grid__customer-text">
        <span className="portal-data-grid__customer-name">{displayName}</span>
        {email ? <span className="portal-data-grid__customer-email">{email}</span> : null}
      </span>
    </div>
  );
}

export function WithdrawalsDataGrid({
  title,
  requests,
  totalCount,
  loading,
  search,
  onSearchChange,
  page,
  totalPages,
  onPageChange,
  dateLocale
}) {
  const { t } = useLocale();
  const pageItems = buildPageItems(page, totalPages);

  return (
    <PortalPanel className="portal-data-grid">
      <div className="portal-data-grid__toolbar">
        <div className="portal-data-grid__title-row">
          <h2 className="portal-panel__title">{title}</h2>
          <span className="portal-data-grid__count">{totalCount}</span>
        </div>

        <div className="portal-data-grid__search">
          <Search size={16} aria-hidden="true" className="portal-data-grid__search-icon" />
          <input
            type="search"
            className="portal-data-grid__search-field"
            aria-label={t("withdrawals.table.search")}
            placeholder={t("withdrawals.table.search")}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
      </div>

      <div className="portal-data-grid__table-wrap">
        {loading && requests.length === 0 ? (
          <div className="portal-data-grid__loading">
            <Spinner size="md" />
            <span>{t("common.loading")}</span>
          </div>
        ) : (
          <Table variant="secondary" className="portal-data-grid__table">
            <Table.ScrollContainer>
              <Table.Content aria-label={title}>
                <Table.Header>
                  <Table.Column isRowHeader>{t("withdrawals.table.customer")}</Table.Column>
                  <Table.Column>{t("withdrawals.table.status")}</Table.Column>
                  <Table.Column>{t("withdrawals.table.date")}</Table.Column>
                  <Table.Column>{t("withdrawals.table.items")}</Table.Column>
                  <Table.Column>{t("withdrawals.table.order")}</Table.Column>
                  <Table.Column align="end">{t("withdrawals.table.actions")}</Table.Column>
                </Table.Header>
                <Table.Body
                  items={requests}
                  renderEmptyState={() => (
                    <div className="portal-data-grid__empty">
                      <p className="portal-data-grid__empty-title">{t("withdrawals.table.empty")}</p>
                      <p className="portal-data-grid__empty-description">{t("withdrawals.table.emptyHint")}</p>
                    </div>
                  )}
                >
                  {(row) => (
                    <Table.Row id={String(row.id)}>
                      <Table.Cell>
                        <CustomerCell row={row} />
                      </Table.Cell>
                      <Table.Cell>
                        <StatusChip status={row.status} label={withdrawalStatusLabel(t, row.status)} />
                      </Table.Cell>
                      <Table.Cell className="portal-data-grid__date">
                        {formatDate(row.submitted_at, dateLocale)}
                      </Table.Cell>
                      <Table.Cell className="portal-data-grid__items">{row.items_summary || "—"}</Table.Cell>
                      <Table.Cell className="portal-data-grid__order">
                        {row.order_number ? `#${row.order_number}` : t("withdrawals.table.noOrder")}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="portal-data-grid__actions">
                          <Button
                            isIconOnly
                            size="sm"
                            variant="secondary"
                            aria-label={t("withdrawals.table.details")}
                            onPress={() => navigatePortal(`/portal/withdrawals/${row.id}`)}
                          >
                            <Eye size={16} />
                          </Button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        )}
      </div>

      <div className="portal-data-grid__footer">
        <p className="portal-data-grid__meta">
          {t("withdrawals.table.showing", { shown: requests.length, total: totalCount })}
        </p>

        {totalPages > 1 ? (
          <Pagination className="portal-pagination">
            <Pagination.Summary className="portal-pagination__summary">
              {t("withdrawals.table.page", { page, pages: totalPages })}
            </Pagination.Summary>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous isDisabled={page <= 1} onPress={() => onPageChange(page - 1)}>
                  <Pagination.PreviousIcon />
                  <span>{t("extensionTexts.previous")}</span>
                </Pagination.Previous>
              </Pagination.Item>
              {pageItems.map((item) =>
                typeof item === "number" ? (
                  <Pagination.Item key={item}>
                    <Pagination.Link isActive={item === page} onPress={() => onPageChange(item)}>
                      {item}
                    </Pagination.Link>
                  </Pagination.Item>
                ) : (
                  <Pagination.Item key={item}>
                    <Pagination.Ellipsis />
                  </Pagination.Item>
                )
              )}
              <Pagination.Item>
                <Pagination.Next isDisabled={page >= totalPages} onPress={() => onPageChange(page + 1)}>
                  <span>{t("extensionTexts.next")}</span>
                  <Pagination.NextIcon />
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        ) : null}
      </div>
    </PortalPanel>
  );
}
